import type { CSSProperties } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiGet, apiPost, TENANT_TOKEN_KEY } from '../../../api/client';
import { tokens } from '../../../design-system/tokens';
import { chatLinkUnlessDeepLink } from './conversationIdFromDeepLink';

export type OperatorNotificationRow = {
  id: string;
  type: string;
  status: string;
  title: string;
  body: string | null;
  deepLinkPath: string;
  payload: unknown;
  basePriority: number;
  escalationLevel: number;
  effectivePriority: number;
  createdAt: string;
  updatedAt: string;
  resolvedAt: string | null;
  dismissedAt: string | null;
  backgroundColor: string;
};

function formatTime(iso: string): string {
  try {
    const d = new Date(iso);
    const now = Date.now();
    const diffM = Math.floor((now - d.getTime()) / 60000);
    if (diffM < 1) return 'ahora';
    if (diffM < 60) return `hace ${diffM} min`;
    const diffH = Math.floor(diffM / 60);
    if (diffH < 24) return `hace ${diffH} h`;
    return d.toLocaleDateString();
  } catch {
    return '';
  }
}

type NotificationTypeOption = { type: string; label: string };

export function OperatorNotificationsPage() {
  const token = typeof window !== 'undefined' ? localStorage.getItem(TENANT_TOKEN_KEY) : null;
  const [items, setItems] = useState<OperatorNotificationRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [includeDismissed, setIncludeDismissed] = useState(false);
  const [includeResolved, setIncludeResolved] = useState(false);
  const [q, setQ] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [typeOptions, setTypeOptions] = useState<NotificationTypeOption[]>([]);
  const qRef = useRef(q);
  qRef.current = q;

  const refreshList = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (includeDismissed) qs.set('includeDismissed', '1');
      if (includeResolved) qs.set('includeResolved', '1');
      if (qRef.current.trim()) qs.set('q', qRef.current.trim());
      if (typeFilter.trim()) qs.set('type', typeFilter.trim());
      qs.set('take', '80');
      const data = await apiGet<{ items: OperatorNotificationRow[]; total: number }>(
        `/api/console/notifications?${qs.toString()}`,
        token,
        { showErrorGrowl: false }
      );
      setItems(data.items);
      setTotal(data.total);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [token, includeDismissed, includeResolved, typeFilter]);

  useEffect(() => {
    void refreshList();
  }, [refreshList]);

  useEffect(() => {
    if (!token) return;
    void (async () => {
      try {
        const data = await apiGet<{ items: NotificationTypeOption[] }>(
          '/api/console/notifications/types',
          token,
          { showErrorGrowl: false }
        );
        setTypeOptions(data.items);
      } catch {
        setTypeOptions([]);
      }
    })();
  }, [token]);

  const typeLabelByCode = useMemo(() => {
    const m = new Map<string, string>();
    for (const t of typeOptions) m.set(t.type, t.label);
    return m;
  }, [typeOptions]);

  const onDismiss = async (id: string) => {
    if (!token) return;
    await apiPost(`/api/console/notifications/${id}/dismiss`, {}, token, { showErrorGrowl: true });
    void refreshList();
    window.dispatchEvent(new Event('va-operator-notifications-updated'));
  };

  const onUndismiss = async (id: string) => {
    if (!token) return;
    await apiPost(`/api/console/notifications/${id}/undismiss`, {}, token, { showErrorGrowl: true });
    void refreshList();
    window.dispatchEvent(new Event('va-operator-notifications-updated'));
  };

  const onResolve = async (id: string) => {
    if (!token) return;
    await apiPost(`/api/console/notifications/${id}/resolve`, {}, token, {
      showErrorGrowl: true,
      successMessage: 'Marcada como resuelta',
    });
    void refreshList();
    window.dispatchEvent(new Event('va-operator-notifications-updated'));
  };

  const onDeclineBooking = async (id: string) => {
    if (!token) return;
    if (!window.confirm('¿No reservar? Se libera la seña pendiente y el chat vuelve a automático.')) return;
    await apiPost(`/api/console/notifications/${id}/decline-booking`, {}, token, {
      showErrorGrowl: true,
      successMessage: 'Se liberó la seña y el chat volvió a automático',
    });
    void refreshList();
    window.dispatchEvent(new Event('va-operator-notifications-updated'));
  };

  const onReopen = async (id: string) => {
    if (!token) return;
    await apiPost(`/api/console/notifications/${id}/reopen`, {}, token, {
      showErrorGrowl: true,
      successMessage: 'Notificación reabierta',
    });
    void refreshList();
    window.dispatchEvent(new Event('va-operator-notifications-updated'));
  };

  const linkBtn: CSSProperties = {
    fontSize: 14,
    fontWeight: 600,
    color: tokens.colors.accent[600],
    textDecoration: 'none',
    padding: 0,
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    fontFamily: 'inherit',
  };

  const textBtn: CSSProperties = {
    fontSize: 14,
    border: 'none',
    background: 'transparent',
    color: tokens.semantic.textSecondary,
    cursor: 'pointer',
    textDecoration: 'underline',
    padding: 0,
    fontFamily: 'inherit',
  };

  return (
    <div
      style={{
        padding: '24px 28px 40px',
        maxWidth: 900,
        margin: '0 auto',
        color: tokens.semantic.textPrimary,
      }}
    >
      <h1 style={{ fontSize: 22, fontWeight: 700, margin: '0 0 8px' }}>Notificaciones</h1>
      <p style={{ fontSize: 14, color: tokens.semantic.textMuted, margin: '0 0 24px' }}>
        Alertas y avisos para operadores. Para activar o silenciar tipos de alerta, ve a{' '}
        <Link to="/app/settings/notifications" style={{ color: tokens.colors.accent[600], fontWeight: 600 }}>
          Alertas (config)
        </Link>
        .
      </p>

      <div
        style={{
          padding: 16,
          borderRadius: 12,
          border: `1px solid ${tokens.semantic.border}`,
          background: tokens.semantic.surface,
          marginBottom: 20,
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
        }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end' }}>
          <label style={{ flex: '1 1 200px', display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12, fontWeight: 600 }}>
            Buscar
            <input
              type="search"
              placeholder="Texto en título o cuerpo…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void refreshList();
              }}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: 8,
                border: `1px solid ${tokens.semantic.border}`,
                fontSize: 14,
              }}
            />
          </label>
          <label style={{ flex: '1 1 200px', display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12, fontWeight: 600 }}>
            Tipo
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: 8,
                border: `1px solid ${tokens.semantic.border}`,
                fontSize: 14,
                background: tokens.semantic.surface,
                color: tokens.semantic.textPrimary,
              }}
            >
              <option value="">Todos los tipos</option>
              {typeOptions.map((t) => (
                <option key={t.type} value={t.type}>
                  {t.label}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            onClick={() => void refreshList()}
            style={{
              padding: '10px 16px',
              borderRadius: 8,
              border: `1px solid ${tokens.semantic.border}`,
              background: tokens.semantic.surfaceSubtle,
              cursor: 'pointer',
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            Aplicar filtros
          </button>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center', fontSize: 13 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input type="checkbox" checked={includeDismissed} onChange={(e) => setIncludeDismissed(e.target.checked)} />
            Mostrar descartadas
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input type="checkbox" checked={includeResolved} onChange={(e) => setIncludeResolved(e.target.checked)} />
            Mostrar resueltas
          </label>
          <span style={{ color: tokens.semantic.textMuted }}>Total: {total}</span>
        </div>
      </div>

      <div
        style={{
          borderRadius: 12,
          border: `1px solid ${tokens.semantic.border}`,
          background: tokens.semantic.surface,
          overflow: 'hidden',
        }}
      >
        {loading && (
          <div style={{ padding: 24, fontSize: 14, color: tokens.semantic.textMuted }}>Cargando…</div>
        )}
        {!loading && items.length === 0 && (
          <div style={{ padding: 24, fontSize: 14, color: tokens.semantic.textMuted }}>No hay notificaciones.</div>
        )}
        {!loading &&
          items.map((n) => (
            <div
              key={n.id}
              style={{
                padding: '16px 18px',
                borderBottom: `1px solid ${tokens.semantic.border}`,
                background: n.backgroundColor || tokens.semantic.surface,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 6 }}>{n.title}</div>
                  {n.body && (
                    <div style={{ fontSize: 14, color: tokens.semantic.textSecondary, marginBottom: 8 }}>{n.body}</div>
                  )}
                  <div style={{ fontSize: 12, color: tokens.semantic.textMuted }}>
                    {formatTime(n.updatedAt)}
                    {n.status === 'resolved' ? ' · Resuelta' : ''}
                    {n.escalationLevel > 0 ? ` · Prioridad +${n.escalationLevel}` : ''}
                  </div>
                  <div style={{ fontSize: 12, color: tokens.semantic.textMuted, marginTop: 6 }}>
                    {typeLabelByCode.get(n.type) ?? n.type}
                  </div>
                </div>
              </div>
              <div
                role="group"
                aria-label="Acciones"
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: '4px 14px',
                  marginTop: 14,
                  paddingTop: 12,
                  borderTop: `1px solid ${tokens.semantic.border}`,
                }}
              >
                <Link to={n.deepLinkPath} style={linkBtn}>
                  Abrir detalle
                </Link>
                {chatLinkUnlessDeepLink(n.deepLinkPath, n.payload) ? (
                  <Link to={chatLinkUnlessDeepLink(n.deepLinkPath, n.payload)!} style={linkBtn}>
                    Ver chat
                  </Link>
                ) : null}
                {n.status === 'open' && n.type === 'calendar.booking_visitor_confirmed' ? (
                  <button type="button" onClick={() => void onDeclineBooking(n.id)} style={textBtn}>
                    No reservar
                  </button>
                ) : null}
                {n.status === 'open' ? (
                  <button type="button" onClick={() => void onResolve(n.id)} style={textBtn}>
                    Marcar como resuelta
                  </button>
                ) : (
                  <button type="button" onClick={() => void onReopen(n.id)} style={textBtn}>
                    Reabrir
                  </button>
                )}
                {!n.dismissedAt ? (
                  <button type="button" onClick={() => void onDismiss(n.id)} style={textBtn}>
                    Descartar (solo para mí)
                  </button>
                ) : (
                  <button type="button" onClick={() => void onUndismiss(n.id)} style={textBtn}>
                    Dejar de descartar
                  </button>
                )}
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}
